import type { EventQuery, EventWithTicketsQuery } from "../generated/graphql"

const EVENT_QUERY_DATA_PREFIX = "werz:eventQueryData:"
const EVENT_WITH_TICKETS_DATA_PREFIX = "werz:eventWithTicketsData:"

const getScopedEventKey = (prefix: string, eventId: string, communityId?: string) =>
  `${prefix}${communityId ? `${communityId}:` : ""}${eventId}`

const getEventQueryDataKey = (eventId: string, communityId?: string) =>
  getScopedEventKey(EVENT_QUERY_DATA_PREFIX, eventId, communityId)
const getEventWithTicketsDataKey = (eventId: string, communityId?: string) =>
  getScopedEventKey(EVENT_WITH_TICKETS_DATA_PREFIX, eventId, communityId)

const saveSessionData = (key: string, payload: unknown) => {
  if (typeof window === "undefined") return

  try {
    window.sessionStorage.setItem(key, JSON.stringify(payload))
  } catch {
    // Session storage can be unavailable in restricted browser modes.
  }
}

const readSessionData = <TData>(key: string) => {
  if (typeof window === "undefined") return undefined

  try {
    const storedValue = window.sessionStorage.getItem(key)
    if (!storedValue) {
      return undefined
    }

    return JSON.parse(storedValue) as TData
  } catch {
    return undefined
  }
}

export const saveEventQueryData = (eventId: string, data: EventQuery, communityId?: string) => {
  if (!eventId) return
  saveSessionData(getEventQueryDataKey(eventId, communityId), data)
}

export const readEventQueryData = (eventId: string, communityId?: string) => {
  if (!eventId) return undefined
  return readSessionData<EventQuery>(getEventQueryDataKey(eventId, communityId))
}

export const saveEventWithTicketsData = (
  eventId: string,
  data: EventWithTicketsQuery,
  communityId?: string,
) => {
  if (!eventId) return
  saveSessionData(getEventWithTicketsDataKey(eventId, communityId), data)
}

export const readEventWithTicketsData = (eventId: string, communityId?: string) => {
  if (!eventId) return undefined
  return readSessionData<EventWithTicketsQuery>(getEventWithTicketsDataKey(eventId, communityId))
}

export const clearEventWithTicketsData = () => {
  if (typeof window === "undefined") return

  try {
    for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = window.sessionStorage.key(index)
      if (key?.startsWith(EVENT_WITH_TICKETS_DATA_PREFIX)) {
        window.sessionStorage.removeItem(key)
      }
    }
  } catch {
    // Session storage can be unavailable in restricted browser modes.
  }
}
